import React, { useEffect, useState } from "react";
import { Button, Empty, Skeleton, Badge, Dropdown } from "antd";
import { ReloadOutlined, SwapOutlined, BarChartOutlined, FileTextOutlined, BellOutlined, PhoneOutlined, WifiOutlined, CreditCardOutlined, RiseOutlined, EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDashboard } from "../../../store/context/DashboardContext";
import { useTranslation } from "react-i18next";
import { useFeatures } from "../../../store/context/feature-context";
import { featuresEnum } from "../../../shared/constants/constants";
import { useDashboardLayout } from "../../../store/context/dashboard-layout-context";
import SelectAccoutModal from "../../Payment Methods/Modals/SelectAccoutModal";
import DashboardCarousel from "../DashboardCarousel";
import DepositCard from "../../Policies/DepositCard";
import PolicyCard from "../../Policies/PolicyCard";
import { formatCurrencyWithoutCode, formatDate } from "../../../utils/helper/format";
import { getGreeting } from "../../../utils/helper/getGreeting";
import { darkenHex, hexToRgba } from "../../../utils/helper/colorUtils";
import { useTenant } from "../../../store/context/tenant-context";

/* Shared glass card style � subtle frosted look, no cheap glow blobs */
const glassCard = {
  background: "rgba(255,255,255,0.06)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 18,
};

/**
 * Design 2 � Dark Glass layout.
 * Deep forest-dark background (matches sidebar), frosted cards,
 * clean typography � no radial blobs, no cheap multi-colour gradients.
 */
const GlassmorphismLayout = ({
  savedSessionData,
  notificationMenu,
  notificationList,
  layoutSwitcher,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { findFeature } = useFeatures();
  const {
    selectedFilter,
    setSelectedFilter,
    policies,
    fetchPolicies,
    pendingQuotes,
    loading,
    depositPolicies,
    bankingProfile,
  } = useDashboard();

  const requiresAccount = bankingProfile?.hasCoreBankingAccounts === false;

  const { balanceVisible, toggleBalanceVisible } = useDashboardLayout();
  const tenant = useTenant();
  const primary = tenant?.theme?.colorPrimary || "#e5791b";
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState(null);

  useEffect(() => {
    if (depositPolicies.length > 0 && !selectedAccountNumber) {
      setSelectedAccountNumber(depositPolicies[0].accountNumber);
    }
  }, [depositPolicies, selectedAccountNumber]);

  const selectedAccount =
    depositPolicies.find((a) => a.accountNumber === selectedAccountNumber) ||
    depositPolicies[0];

  const selectedBalance =
    selectedAccount?.availableBalance || selectedAccount?.accountBalance || 0;

  const categories = [
    { key: "deposits", label: t("Deposits") },
    { key: "loans", label: t("Loans") },
    { key: "investments", label: t("Investments") },
  ].filter((c) => {
    if (c.key === "loans") return findFeature(featuresEnum.CUSTOMER_LOAN);
    return true;
  });

  const filteredPolicies = policies
    .slice()
    .sort((a, b) => a.type.localeCompare(b.type))
    .filter((p) => p.type.includes(selectedFilter));

  const stats = [
    { label: t("Active Loans"), value: policies.filter((p) => p.type === "loans").length, IconCmp: CreditCardOutlined, color: "#4ade80", accent: "rgba(74,222,128,0.18)" },
    { label: t("Savings Rate"), value: "10% p.a.", IconCmp: RiseOutlined, color: primary, accent: hexToRgba(primary, 0.18) },
    { label: t("Investments"), value: policies.filter((p) => p.type === "investments").length, IconCmp: BarChartOutlined, color: "#60a5fa", accent: "rgba(96,165,250,0.18)" },
  ];

  const quickActions = [
    { IconCmp: PhoneOutlined, label: t("Airtime"), path: "/airtime" },
    { IconCmp: WifiOutlined, label: t("Data"), path: "/data" },
    { IconCmp: CreditCardOutlined, label: t("Bills"), path: "/bills-payment" },
    { IconCmp: SwapOutlined, label: t("Transfer"), path: "/transfer" },
  ];

  return (
    /* Root bg: matches the CSS dark sidebar � seamless dark experience */
    <div style={{ background: "#111b15", minHeight: "100vh" }}>
      {/* topbar removed — InnerLayout now renders the unified header */}
      <div style={{ padding: "24px 28px", overflow: "hidden" }}>
        <SelectAccoutModal
          isModalVisible={isSwitchModalOpen}
          handleCancel={() => setIsSwitchModalOpen(false)}
          handleOk={(accountNumber) => {
            setSelectedAccountNumber(accountNumber);
            setIsSwitchModalOpen(false);
          }}
        />
        {/* -- Balance card -- */} � clean dark card, single accent line -- */}
        <div
          style={{
            ...glassCard,
            padding: "28px 32px",
            marginBottom: 20,
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16,
            borderLeft: `3px solid ${primary}`,
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "1.5px", margin: 0 }}>
                {t("Account Balance")}
              </p>
              <button
                onClick={() => toggleBalanceVisible()}
                style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, padding: "2px 8px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, color: "rgba(255,255,255,0.7)", fontSize: 11 }}
              >
                {balanceVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                {balanceVisible ? t("Hide") : t("Show")}
              </button>
            </div>
            <p style={{ fontSize: 40, fontWeight: 800, color: "#fff", margin: "0 0 10px", letterSpacing: "-1.5px" }}>
              {"\u20A6"}{balanceVisible ? formatCurrencyWithoutCode(selectedBalance) : "***.** "}
            </p>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", fontWeight: 500 }}>
                {selectedAccount?.accountType || t("Savings")}
              </span>
              {selectedAccount?.accountNumber && (
                <span style={{ fontSize: 12, color: "#fff", background: "rgba(255,255,255,0.12)", padding: "3px 10px", borderRadius: 20, fontFamily: "monospace", letterSpacing: "1px", fontWeight: 600, border: "1px solid rgba(255,255,255,0.2)", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: selectedAccount.active ? "#4ade80" : "#f87171", flexShrink: 0, boxShadow: selectedAccount.active ? "0 0 6px #4ade80" : "0 0 6px #f87171" }} />
                  {selectedAccount.accountNumber}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, flexDirection: "column" }}>
            <Button icon={<SwapOutlined />} onClick={() => navigate("/transfer")} style={{ background: primary, border: "none", color: "#fff", borderRadius: 10, height: 40, fontWeight: 600 }}>{t("Transfer")}</Button>
            <Button icon={<FileTextOutlined />} onClick={() => navigate("/account-statement")} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.8)", borderRadius: 10, height: 40 }}>{t("Statement")}</Button>
            {depositPolicies.length > 1 && (
              <Button onClick={() => setIsSwitchModalOpen(true)} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.8)", borderRadius: 10, height: 40 }}>{t("Switch Account")}</Button>
            )}
          </div>
        </div>

        {/* -- Stats ----------------------------------------- */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 20 }}>
          {stats.map((s) => (
            <div key={s.label} style={{ ...glassCard, padding: "16px 18px", display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: s.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <s.IconCmp style={{ fontSize: 20, color: s.color }} />
              </div>
              <div>
                <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: ".6px", margin: 0 }}>{s.label}</p>
                <p style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0 }}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* -- Main grid ------------------------------------- */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0, overflow: "hidden" }}>
            {!!pendingQuotes?.length && (
              <div style={{ ...glassCard, padding: "20px 22px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <p style={{ fontWeight: 700, fontSize: 14, color: "#fff", margin: 0 }}>{t("My Pending Applications")}</p>
                  <button onClick={() => navigate("/loans")} style={{ fontSize: 12, color: "#4ade80", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>
                    {t("View all")} →
                  </button>
                </div>
                {pendingQuotes.slice(0, 3).map((quote, i) => (
                  <div key={i} onClick={() => navigate(`/loans/${quote.id}`)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 0", borderBottom: i < Math.min(pendingQuotes.length, 3) - 1 ? "1px solid rgba(255,255,255,0.06)" : "none", cursor: "pointer" }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <FileTextOutlined style={{ color: "rgba(255,255,255,0.7)", fontSize: 16 }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontWeight: 600, fontSize: 13, color: "#fff", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{quote.accountType || t("Loan")}</p>
                      <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", margin: 0 }}>Loan · {formatDate(quote.dateOpened)}</p>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: "#fff", margin: 0 }}>₦{parseFloat(quote.loanAmount || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      <span style={{ fontSize: 10, padding: "2px 8px", background: "rgba(229,121,27,0.2)", color: "#e5791b", borderRadius: 20, fontWeight: 600, display: "inline-block", marginTop: 2 }}>Pending</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* My Accounts */}
            <div style={{ ...glassCard, padding: "20px 22px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <p style={{ fontWeight: 700, fontSize: 14, color: "#fff", margin: 0 }}>{t("My Accounts")}</p>
                <Button icon={<ReloadOutlined />} size="small" type="text" onClick={fetchPolicies} style={{ color: "rgba(255,255,255,0.4)" }} />
              </div>
              <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                {categories.map((c) => (
                  <button
                    key={c.key}
                    disabled={requiresAccount && c.key !== "loans"}
                    onClick={() => setSelectedFilter(c.key)}
                    style={{
                      padding: "6px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                      cursor: "pointer", border: "none", transition: "all .15s",
                      background: selectedFilter === c.key ? "#e5791b" : "rgba(255,255,255,0.08)",
                      color: selectedFilter === c.key ? "#fff" : "rgba(255,255,255,0.5)",
                    }}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              {loading ? (
                <Skeleton active paragraph={{ rows: 3 }} />
              ) : filteredPolicies.length > 0 ? (
                <div style={{ overflow: "hidden" }}>
                  <DashboardCarousel
                    slides={filteredPolicies.map((policy) =>
                      policy.type === "deposits" || policy.type === "loans" ? (
                        <DepositCard key={policy.policyNumber} policy={policy} balanceVisible={balanceVisible} />
                      ) : (
                        <PolicyCard key={policy.policyNumber} policy={policy} />
                      ),
                    )}
                  />
                </div>
              ) : (
                <Empty description={<span style={{ color: "rgba(255,255,255,0.35)" }}>{`${t("No")} ${selectedFilter} ${t("available")}`}</span>} />
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ ...glassCard, padding: "20px" }}>
            <p style={{ fontWeight: 700, fontSize: 14, color: "#fff", margin: "0 0 16px" }}>{t("Quick Actions")}</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {quickActions.map((a) => (
                <button
                  key={a.label}
                  onClick={() => navigate(a.path)}
                  style={{
                    ...glassCard, border: "1px solid rgba(255,255,255,0.08)",
                    padding: "16px 8px", display: "flex", flexDirection: "column",
                    alignItems: "center", gap: 8, cursor: "pointer",
                    transition: "all .2s",
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
                  onMouseOut={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.06)"}
                >
                  <a.IconCmp style={{ fontSize: 22, color: "rgba(255,255,255,0.8)" }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.8)" }}>{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlassmorphismLayout;
